/**
 * KAN-648: видеозапись Telegram-аккаунта (ссылка и файл) и признак 2FA при
 * создании. Проверки на клиенте повторяют серверные (сервер проверяет всё сам).
 */

export const VIDEO_MAX_BYTES = 200 * 1024 * 1024;
export const VIDEO_EXTENSIONS = [
  'mp4',
  'm4v',
  'mov',
  'webm',
  'mkv',
  'avi',
  '3gp',
  'mpeg',
  'mpg',
] as const;
/** accept для input type=file. */
export const VIDEO_ACCEPT = [
  'video/*',
  ...VIDEO_EXTENSIONS.map((ext) => `.${ext}`),
].join(',');

/** Текст ошибки для выбранного файла видео; null если файл годится. */
export function validateVideoFile(file: {
  name: string;
  type: string;
  size: number;
}): string | null {
  if (!file.size) return 'Видео: пустой файл.';
  if (file.size > VIDEO_MAX_BYTES) return 'Видео: файл больше 200 МБ.';
  const ext = file.name.includes('.')
    ? file.name.split('.').pop()?.toLowerCase() ?? ''
    : '';
  if (!(VIDEO_EXTENSIONS as readonly string[]).includes(ext)) {
    return `Видео: ожидается файл ${VIDEO_EXTENSIONS.join(', ')}.`;
  }
  if (!file.type.toLowerCase().startsWith('video/')) {
    return 'Видео: тип файла должен быть video/*.';
  }
  return null;
}

/** Текст ошибки для ссылки на видео; пусто допустимо (очистить). */
export function validateVideoUrl(value: string): string | null {
  const text = value.trim();
  if (!text) return null;
  if (/\s/.test(text)) return 'Ссылка на видео: пробелы недопустимы.';
  try {
    const url = new URL(text);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return 'Ссылка на видео: ожидается адрес http:// или https://.';
    }
  } catch {
    return 'Ссылка на видео: ожидается адрес http:// или https://.';
  }
  return null;
}

export interface TwoFaBadgeView {
  label: string;
  title: string;
  className: string;
}

/** Бейдж признака "2FA при создании": "2FA" / "без 2FA" / "?". */
export function twoFaBadge(value: boolean | null | undefined): TwoFaBadgeView {
  if (value === true) {
    return {
      label: '2FA',
      title: 'При подключении потребовался пароль двухэтапной проверки',
      className: 'bg-green-100 text-green-800',
    };
  }
  if (value === false) {
    return {
      label: 'без 2FA',
      title: 'При подключении пароль двухэтапной проверки не понадобился',
      className: 'bg-gray-100 text-gray-700',
    };
  }
  return {
    label: '?',
    title: 'Неизвестно, был ли пароль двухэтапной проверки при подключении',
    className: 'bg-yellow-50 text-yellow-800',
  };
}

/** Значение select 2FA <-> признак. */
export type TwoFaChoice = 'yes' | 'no' | 'unknown';

export function twoFaToChoice(value: boolean | null | undefined): TwoFaChoice {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return 'unknown';
}

export function choiceToTwoFa(choice: TwoFaChoice): boolean | null {
  if (choice === 'yes') return true;
  if (choice === 'no') return false;
  return null;
}

export interface VideoDraft {
  url: string;
  file: File | null;
}

export const EMPTY_VIDEO_DRAFT: VideoDraft = { url: '', file: null };

/** Черновик годится для отправки (ссылка пустая или корректная). */
export function isVideoDraftValid(draft: VideoDraft): boolean {
  return validateVideoUrl(draft.url) === null;
}

interface VideoApi {
  updateAccount: (
    accountId: string,
    body: { video_url: string },
    operatorId?: string
  ) => Promise<unknown>;
  uploadVideo: (
    accountId: string,
    file: File,
    operatorId?: string
  ) => Promise<void>;
}

/**
 * Сохранить видео только что подключённого аккаунта: ссылку (PATCH) и файл
 * (POST video/). Пустой черновик ничего не отправляет.
 */
export async function saveVideoDraft(
  api: VideoApi,
  accountId: string,
  draft: VideoDraft,
  operatorId?: string
): Promise<void> {
  const url = draft.url.trim();
  if (url) await api.updateAccount(accountId, { video_url: url }, operatorId);
  if (draft.file) await api.uploadVideo(accountId, draft.file, operatorId);
}

/**
 * KAN-785: откуда брать видео для вкладки. S3: сервер отдаёт JSON {url}
 * (presigned адрес); локальное хранилище (прод): сам файл, из него делается
 * blob-адрес на клиенте.
 */
export type VideoSource =
  | { kind: 'url'; url: string }
  | { kind: 'blob'; blob: Blob };

function isJsonContentType(contentType: string | undefined): boolean {
  return (contentType ?? '').toLowerCase().includes('application/json');
}

function urlFromJson(value: unknown): string | null {
  if (value && typeof value === 'object' && 'url' in value) {
    const url = (value as { url?: unknown }).url;
    if (typeof url === 'string' && url.trim()) return url;
  }
  return null;
}

/** Текст Blob; Blob.text() есть не везде (jsdom), тогда FileReader. */
function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * Ответ GET video/ (responseType blob) -> источник видео. JSON (по Content-Type
 * или уже разобранный объект) даёт {kind: 'url'}, любой другой Blob - {kind: 'blob'}.
 * Ошибка, если в JSON нет url или тело не файл.
 */
export async function videoResponseToSource(
  contentType: string | undefined,
  data: unknown
): Promise<VideoSource> {
  const asJson = isJsonContentType(contentType) || !(data instanceof Blob);
  if (asJson) {
    let parsed: unknown = data;
    if (data instanceof Blob) {
      try {
        parsed = JSON.parse(await readBlobText(data));
      } catch {
        throw new Error('Сервер вернул нечитаемый ответ вместо видео');
      }
    }
    const url = urlFromJson(parsed);
    if (!url) throw new Error('Сервер не вернул адрес видео');
    return { kind: 'url', url };
  }
  return { kind: 'blob', blob: data };
}

/**
 * Id аккаунта из ответа подключения: держатель отдаёт `id`, список - `account_id`.
 */
export function connectedAccountId(account: {
  account_id?: string | null;
  id?: string | number | null;
}): string {
  return String(account.account_id || account.id || '').trim();
}
